/**
 * Tedarikçi detay sayfası — Sprint 37.
 *
 * 4 sekme: Genel, Cari Ekstre, Çek/Senet, Satın Alma Geçmişi.
 * Üstte bakiye kartı (balance / kredi limiti / vadesi geçen / portföy çek).
 */
import {
  ActionIcon, Alert, Badge, Button, Card, FileButton, Group, Loader, Modal,
  NumberInput, Select, Stack, Switch, Table, Tabs, Text, TextInput, Textarea, Title,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useDisclosure } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import {
  IconAlertTriangle, IconArrowLeft, IconCash, IconCheck,
  IconFileInvoice, IconPlus, IconReceipt2, IconX,
} from "@tabler/icons-react";
import { useState } from "react";
import {
  useChangeInstrumentStatus, useCreateInstrument, useInstruments, useUploadInstrumentImage,
  useReverseAdjustment,
  useResource, useSupplierAdjustment, useSupplierBalance,
  useSupplierLedger, useSupplierOpeningBalance, useUpdate,
  useRecordSupplierPayment,
} from "../../../shared/api";
import { MoneyDisplay, formatDate, formatDateTime, validateEmail, validatePhoneTR } from "../../../shared/ui";
import { PurchaseOrderDetailDrawer } from "./PurchaseOrders";
import { GoodsReceiptDetailDrawer } from "./GoodsReceipts";
import { SupplierInvoiceDetailModal } from "../components/SupplierInvoiceDetailModal";
import { confirmAction } from "../../../shared/ui/confirm";

const ENTRY_TYPE_COLORS: Record<string, string> = {
  OPENING_BALANCE: "blue",
  INVOICE: "red",
  PAYMENT: "green",
  CHECK_ISSUED: "orange",
  CHECK_CLEARED: "gray",
  CHECK_BOUNCED: "red",
  NOTE_ISSUED: "orange",
  NOTE_CLEARED: "gray",
  NOTE_BOUNCED: "red",
  ADJUSTMENT: "yellow",
  REFUND: "green",
  INVOICE_CANCELLED: "gray",
  GOODS_RECEIPT: "grape",
  GOODS_RECEIPT_REVERSED: "gray",
};

const INSTRUMENT_STATUS_COLORS: Record<string, string> = {
  PORTFOLIO: "blue",
  ENDORSED: "indigo",
  CLEARED: "green",
  BOUNCED: "red",
  CANCELLED: "gray",
};

interface SupplierDetailProps {
  supplierId: string;
  onBack: () => void;
}

export function SupplierDetail({ supplierId, onBack }: SupplierDetailProps) {
  const { data: supplier, isLoading } = useResource<any>("suppliers", { id: supplierId });
  const { data: balance } = useSupplierBalance(supplierId);

  if (isLoading || !supplier) {
    return (
      <Stack align="center" mt="xl"><Loader /></Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group>
          <ActionIcon variant="subtle" size="lg" onClick={onBack}>
            <IconArrowLeft size={20} />
          </ActionIcon>
          <Title order={3}>{supplier.name}</Title>
          {supplier.tax_id && <Text c="dimmed">VKN: {supplier.tax_id}</Text>}
        </Group>
      </Group>

      <BalanceCard balance={balance} />

      <Tabs defaultValue="ledger" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="ledger" leftSection={<IconReceipt2 size={16} />}>
            Cari Ekstre
          </Tabs.Tab>
          <Tabs.Tab value="instruments" leftSection={<IconFileInvoice size={16} />}>
            Çek / Senet
          </Tabs.Tab>
          <Tabs.Tab value="general">Genel</Tabs.Tab>
          <Tabs.Tab value="history">Satın Alma</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="ledger" pt="md">
          <LedgerTab supplierId={supplierId} hasOpeningBalance={!!balance?.has_opening_balance} />
        </Tabs.Panel>
        <Tabs.Panel value="instruments" pt="md">
          <InstrumentsTab supplierId={supplierId} />
        </Tabs.Panel>
        <Tabs.Panel value="general" pt="md">
          <GeneralTab supplier={supplier} />
        </Tabs.Panel>
        <Tabs.Panel value="history" pt="md">
          <HistoryTab supplierId={supplierId} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Bakiye kartı
// ---------------------------------------------------------------------------
function BalanceCard({ balance }: { balance: any }) {
  if (!balance) {
    return <Card withBorder padding="md"><Loader size="sm" /></Card>;
  }
  const over = balance.over_limit;
  const balanceColor = balance.balance_minor > 0 ? "red" : "green";

  return (
    <Card
      withBorder
      padding="md"
      style={over ? { borderColor: "var(--mantine-color-red-6)", borderWidth: 2 } : undefined}
    >
      {over && (
        <Alert color="red" variant="light" mb="md" icon={<IconAlertTriangle size={16} />}>
          Kredi limiti aşıldı
        </Alert>
      )}
      <Group justify="space-between" wrap="wrap" gap="lg">
        <BalanceCell
          label="Güncel Bakiye"
          value={<MoneyDisplay minor={balance.balance_minor} fw={700} size="xl" c={balanceColor} />}
          hint={balance.balance_minor > 0 ? "Borçlu" : balance.balance_minor < 0 ? "Alacaklı" : "Kapalı"}
        />
        <BalanceCell
          label="Kredi Limiti"
          value={
            balance.credit_limit_minor > 0
              ? <MoneyDisplay minor={balance.credit_limit_minor} />
              : <Text c="dimmed">Limitsiz</Text>
          }
          hint={
            balance.credit_limit_minor > 0
              ? <>Kalan: <MoneyDisplay minor={balance.available_credit_minor} /></>
              : null
          }
        />
        <BalanceCell
          label="Vadesi Geçen"
          value={<MoneyDisplay minor={balance.overdue_minor} fw={600} c={balance.overdue_minor > 0 ? "red" : "dimmed"} />}
        />
        <BalanceCell
          label="Son Ödeme"
          value={
            <Text fw={500}>
              {balance.last_payment_at ? formatDate(balance.last_payment_at) : "—"}
            </Text>
          }
        />
        <BalanceCell
          label="Portföydeki Çek/Senet"
          value={<MoneyDisplay minor={balance.portfolio_instrument_total_minor} fw={500} />}
        />
      </Group>
    </Card>
  );
}

function BalanceCell({ label, value, hint }: { label: string; value: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
      <div style={{ marginTop: 4 }}>{value}</div>
      {hint && <Text size="xs" c="dimmed" mt={2}>{hint}</Text>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cari Ekstre sekmesi
// ---------------------------------------------------------------------------
function LedgerTab({ supplierId, hasOpeningBalance }: { supplierId: string; hasOpeningBalance: boolean }) {
  const { data, isLoading } = useSupplierLedger(supplierId);
  const [adjOpen, adj] = useDisclosure(false);
  const [openOpen, openBal] = useDisclosure(false);
  const [payOpen, pay] = useDisclosure(false);
  const reverseAdj = useReverseAdjustment();
  const [showReversed, setShowReversed] = useState(false);

  const entries: any[] = data?.entries || [];
  // İptal edilen çiftleri (orijinal 'iptal edildi' + 'İptal: ...' ters kayıt)
  // varsayılan gizle — ekstre temiz kalır, iz silinmez (anahtarla görünür).
  // running_balance backend'de tüm satırlardan hesaplı; gizlemek bakiyeyi bozmaz.
  const reversedCount = entries.filter((e) => e.is_reversed || e.is_reversal).length;
  const visibleEntries = showReversed
    ? entries
    : entries.filter((e) => !e.is_reversed && !e.is_reversal);

  function doReverse(entry: any) {
    modals.openConfirmModal({
      title: "Manuel Hareketi İptal Et",
      children: (
        <Text size="sm">
          <b>"{entry.description}"</b> manuel hareketini iptal etmek istiyor musunuz?
          <br />
          Ters kayıt atılır (satır silinmez), bakiye düzeltilir.
        </Text>
      ),
      labels: { confirm: "İptal Et", cancel: "Vazgeç" },
      confirmProps: { color: "red" },
      onConfirm: () =>
        reverseAdj.mutate(
          { supplierId, entryId: entry.id },
          {
            onSuccess: () => notifications.show({ message: "Manuel hareket iptal edildi", color: "green" }),
            onError: (e: any) => notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" }),
          },
        ),
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        {reversedCount > 0 ? (
          <Switch
            size="sm"
            label={`İptal edilenleri göster (${reversedCount})`}
            checked={showReversed}
            onChange={(e) => setShowReversed(e.currentTarget.checked)}
          />
        ) : <span />}
        <Group justify="flex-end" gap="xs">
          {!hasOpeningBalance && (
            <Button variant="default" leftSection={<IconPlus size={16} />} onClick={openBal.open}>
              Açılış Bakiyesi
            </Button>
          )}
          <Button variant="default" leftSection={<IconPlus size={16} />} onClick={adj.open}>
            Manuel Hareket
          </Button>
          <Button color="green" leftSection={<IconPlus size={16} />} onClick={pay.open}>
            Ödeme Yap
          </Button>
        </Group>
      </Group>

      {isLoading ? (
        <Stack align="center"><Loader /></Stack>
      ) : entries.length === 0 ? (
        <Card padding="lg" withBorder>
          <Text ta="center" c="dimmed">Henüz cari hareket yok.</Text>
        </Card>
      ) : (
        <Card padding={0} withBorder>
          <Table.ScrollContainer minWidth={640}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tarih</Table.Th>
                <Table.Th>Tip</Table.Th>
                <Table.Th>Açıklama</Table.Th>
                <Table.Th>Kullanıcı</Table.Th>
                <Table.Th ta="right">Borç</Table.Th>
                <Table.Th ta="right">Alacak</Table.Th>
                <Table.Th ta="right">Bakiye</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {visibleEntries.map((e) => {
                const debit = e.amount_minor > 0 ? e.amount_minor : 0;
                const credit = e.amount_minor < 0 ? -e.amount_minor : 0;
                return (
                  <Table.Tr key={e.id} style={e.is_reversed ? { opacity: 0.55 } : undefined}>
                    <Table.Td>{formatDateTime(e.created_at || e.entry_date)}</Table.Td>
                    <Table.Td>
                      <Badge color={ENTRY_TYPE_COLORS[e.entry_type] || "gray"} variant="light">
                        {e.entry_type_display}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {e.description}
                      {e.is_reversed && (
                        <Badge size="xs" color="gray" variant="outline" ml={6}>iptal edildi</Badge>
                      )}
                      {e.invoice_number && (
                        <Text size="xs" c="dimmed">Fatura: {e.invoice_number}</Text>
                      )}
                      {e.instrument_serial && (
                        <Text size="xs" c="dimmed">Seri: {e.instrument_serial}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {/* Denetim izi: kim girdi — "ben girmedim" tartışmasını önler */}
                      <Text size="xs" {...(e.created_by ? {} : { c: "dimmed" })}>
                        {e.created_by || "sistem"}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      {debit > 0 ? <MoneyDisplay minor={debit} c="red" /> : "—"}
                    </Table.Td>
                    <Table.Td ta="right">
                      {credit > 0 ? <MoneyDisplay minor={credit} c="green" /> : "—"}
                    </Table.Td>
                    <Table.Td ta="right" fw={500}>
                      <MoneyDisplay
                        minor={e.running_balance_minor}
                        {...(e.running_balance_minor > 0
                          ? { c: "red" }
                          : e.running_balance_minor < 0
                            ? { c: "green" }
                            : {})}
                      />
                    </Table.Td>
                    <Table.Td ta="right">
                      {e.can_reverse && (
                        <Tooltip label="Manuel hareketi iptal et (ters kayıt)">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => doReverse(e)}
                            loading={reverseAdj.isPending}
                            aria-label="İptal"
                          >
                            <IconX size={15} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {visibleEntries.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={8} ta="center">
                    <Text size="sm" c="dimmed" py="sm">
                      Tüm hareketler iptal edilmiş. Görmek için "İptal edilenleri göster"i açın.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <AdjustmentModal opened={adjOpen} onClose={adj.close} supplierId={supplierId} />
      <OpeningBalanceModal opened={openOpen} onClose={openBal.close} supplierId={supplierId} />
      <PaymentModal opened={payOpen} onClose={pay.close} supplierId={supplierId} />
    </Stack>
  );
}

// Faturasız (cari hesaba) ödeme — İrsaliye'den doğan borcu fatura beklemeden öde.
// Yöntem Çek/Senet ise portföye NegotiableInstrument kesilir (Çek/Senet tabında
// görünür, CHECK_ISSUED/NOTE_ISSUED ile bakiye düşer). Nakit/Havale/Kart ise
// doğrudan PAYMENT ledger satırı (record_supplier_payment).
function PaymentModal({ opened, onClose, supplierId }: { opened: boolean; onClose: () => void; supplierId: string }) {
  const [amount, setAmount] = useState<number | string>(0);
  const [method, setMethod] = useState<string>("CASH");
  const [reference, setReference] = useState("");
  // Çek/senet alanları
  const [serialNo, setSerialNo] = useState("");
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [bankName, setBankName] = useState("");
  const payMutate = useRecordSupplierPayment();
  const instrMutate = useCreateInstrument();

  const isInstrument = method === "CHECK" || method === "NOTE";
  const pending = payMutate.isPending || instrMutate.isPending;

  function isoDate(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }

  function resetAll() {
    setAmount(0); setReference(""); setSerialNo(""); setDueDate(null); setBankName("");
  }

  async function submit() {
    const minor = Math.round(Number(amount) * 100);
    if (!minor || minor <= 0) {
      notifications.show({ message: "Tutar 0'dan büyük olmalı", color: "red" });
      return;
    }
    try {
      if (isInstrument) {
        if (!serialNo.trim()) { notifications.show({ message: "Seri no girin", color: "orange" }); return; }
        if (!dueDate) { notifications.show({ message: "Vade tarihi girin", color: "orange" }); return; }
        await instrMutate.mutateAsync({
          instrument_type: method === "CHECK" ? "CHECK" : "PROMISSORY_NOTE",
          supplier: supplierId,
          serial_no: serialNo.trim(),
          issue_date: isoDate(new Date()),
          due_date: isoDate(dueDate),
          amount_minor: minor,
          ...(bankName && { bank_name: bankName }),
          ...(reference && { notes: reference }),
        });
        notifications.show({ message: "Çek/senet portföye eklendi", color: "green" });
      } else {
        await payMutate.mutateAsync({
          supplierId, amount_minor: minor, payment_method: method,
          ...(reference && { reference }),
        });
        notifications.show({ message: "Ödeme kaydedildi", color: "green" });
      }
      resetAll();
      onClose();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Tedarikçiye Ödeme" size="md">
      <Stack>
        <Alert variant="light" color="green">
          Fatura beklemeden cari hesaba ödeme. Bakiyeden düşer (−).
          {isInstrument && " Çek/senet portföye eklenir; vade/tahsil takibi Çek/Senet sekmesinden."}
        </Alert>
        <NumberInput
          label="Tutar (TL)"
          value={amount}
          onChange={setAmount}
          min={0}
          decimalScale={2}
          required
        />
        <Select
          label="Ödeme Yöntemi"
          value={method}
          onChange={(v) => setMethod(v || "CASH")}
          data={[
            { value: "CASH", label: "Nakit" },
            { value: "BANK_TRANSFER", label: "Banka Havalesi/EFT" },
            { value: "CHECK", label: "Çek" },
            { value: "NOTE", label: "Senet" },
            { value: "CARD", label: "Kredi Kartı" },
            { value: "OTHER", label: "Diğer" },
          ]}
        />
        {isInstrument && (
          <>
            <Group grow>
              <TextInput
                label="Seri No"
                required
                value={serialNo}
                onChange={(e) => setSerialNo(e.currentTarget.value)}
              />
              <DateInput
                label="Vade Tarihi"
                required
                value={dueDate}
                onChange={(d) => setDueDate(d as Date | null)}
                valueFormat="DD.MM.YYYY"
              />
            </Group>
            {method === "CHECK" && (
              <TextInput
                label="Banka"
                placeholder="Banka adı (opsiyonel)"
                value={bankName}
                onChange={(e) => setBankName(e.currentTarget.value)}
              />
            )}
          </>
        )}
        <TextInput
          label="Referans / Açıklama"
          placeholder="Dekont no, açıklama (opsiyonel)"
          value={reference}
          onChange={(e) => setReference(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button color="green" onClick={submit} loading={pending}>
            {isInstrument ? "Çek/Senet Kes" : "Ödemeyi Kaydet"}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function AdjustmentModal({ opened, onClose, supplierId }: { opened: boolean; onClose: () => void; supplierId: string }) {
  const [amount, setAmount] = useState<number | string>(0);
  const [description, setDescription] = useState("");
  const [direction, setDirection] = useState<"debit" | "credit">("debit");
  const mutate = useSupplierAdjustment();

  async function submit() {
    const rawMinor = Math.round(Number(amount) * 100);
    if (!rawMinor || !description.trim()) {
      notifications.show({ message: "Tutar ve açıklama zorunlu", color: "red" });
      return;
    }
    const signed = direction === "debit" ? Math.abs(rawMinor) : -Math.abs(rawMinor);
    try {
      await mutate.mutateAsync({ supplierId, amount_minor: signed, description: description.trim() });
      notifications.show({ message: "Hareket kaydedildi", color: "green" });
      setAmount(0); setDescription("");
      onClose();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Manuel Cari Hareket" size="md">
      <Stack>
        <Select
          label="Hareket Türü"
          value={direction}
          onChange={(v) => setDirection((v as any) || "debit")}
          data={[
            { value: "debit", label: "Borçlandırma (+) — fiyat farkı, ek tutar" },
            { value: "credit", label: "Alacaklandırma (−) — iskonto, kalem iadesi" },
          ]}
        />
        <NumberInput
          label="Tutar (TL)"
          value={amount}
          onChange={setAmount}
          min={0}
          decimalScale={2}
          required
        />
        <Textarea
          label="Açıklama"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          required
          minRows={2}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button onClick={submit} loading={mutate.isPending}>Kaydet</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function OpeningBalanceModal({ opened, onClose, supplierId }: { opened: boolean; onClose: () => void; supplierId: string }) {
  const [amount, setAmount] = useState<number | string>(0);
  const [direction, setDirection] = useState<"debit" | "credit">("debit");
  const mutate = useSupplierOpeningBalance();

  async function submit() {
    const rawMinor = Math.round(Number(amount) * 100);
    if (!rawMinor) {
      notifications.show({ message: "Tutar 0 olamaz", color: "red" });
      return;
    }
    const signed = direction === "debit" ? Math.abs(rawMinor) : -Math.abs(rawMinor);
    try {
      await mutate.mutateAsync({ supplierId, amount_minor: signed });
      notifications.show({ message: "Açılış bakiyesi kaydedildi", color: "green" });
      setAmount(0);
      onClose();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Açılış Bakiyesi" size="md">
      <Stack>
        <Alert variant="light" color="blue">
          Bu tedarikçi için bir kere girilir — sistem öncesinden devreden borç/alacak.
        </Alert>
        <Select
          label="Devir"
          value={direction}
          onChange={(v) => setDirection((v as any) || "debit")}
          data={[
            { value: "debit", label: "Borçluyuz (+)" },
            { value: "credit", label: "Alacaklıyız (−)" },
          ]}
        />
        <NumberInput
          label="Tutar (TL)"
          value={amount}
          onChange={setAmount}
          min={0}
          decimalScale={2}
          required
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button onClick={submit} loading={mutate.isPending}>Kaydet</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Çek / Senet sekmesi
// ---------------------------------------------------------------------------
function InstrumentsTab({ supplierId }: { supplierId: string }) {
  const { data, isLoading } = useInstruments({ supplier: supplierId });
  const [opened, { open, close }] = useDisclosure(false);
  const changeStatus = useChangeInstrumentStatus();
  const list: any[] = data?.results || data || [];

  async function handleStatusChange(id: string, status: string, label: string) {
    const ok = await confirmAction({
      title: "Durumu Güncelle",
      message: `Çek/senet "${label}" olarak işaretlensin mi?`,
      confirmLabel: "Onayla",
    });
    if (!ok) return;
    try {
      await changeStatus.mutateAsync({ id, status });
      notifications.show({ message: "Durum güncellendi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <Group justify="flex-end">
        <Button leftSection={<IconPlus size={16} />} onClick={open}>
          Yeni Çek / Senet
        </Button>
      </Group>

      {isLoading ? (
        <Stack align="center"><Loader /></Stack>
      ) : list.length === 0 ? (
        <Card padding="lg" withBorder>
          <Text ta="center" c="dimmed">Bu tedarikçiye verilmiş çek/senet yok.</Text>
        </Card>
      ) : (
        <Card padding={0} withBorder>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tip</Table.Th>
                <Table.Th>Seri No</Table.Th>
                <Table.Th>Banka</Table.Th>
                <Table.Th>Vade</Table.Th>
                <Table.Th ta="right">Tutar</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {list.map((it) => (
                <Table.Tr key={it.id}>
                  <Table.Td>{it.instrument_type_display}</Table.Td>
                  <Table.Td fw={500}>{it.serial_no}</Table.Td>
                  <Table.Td>
                    {it.bank_name || "—"}
                    {it.bank_branch && <Text size="xs" c="dimmed">{it.bank_branch}</Text>}
                  </Table.Td>
                  <Table.Td>{formatDate(it.due_date)}</Table.Td>
                  <Table.Td ta="right" fw={500}>
                    <MoneyDisplay minor={it.amount_minor} />
                  </Table.Td>
                  <Table.Td>
                    <Badge color={INSTRUMENT_STATUS_COLORS[it.status] || "gray"} variant="light">
                      {it.status_display}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {(it.status === "PORTFOLIO" || it.status === "ENDORSED") && (
                      <Group gap={4}>
                        <ActionIcon
                          variant="subtle"
                          color="green"
                          title="Tahsil edildi"
                          onClick={() => handleStatusChange(it.id, "CLEARED", "Tahsil Edildi")}
                        >
                          <IconCheck size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          title="Karşılıksız"
                          onClick={() => handleStatusChange(it.id, "BOUNCED", "Karşılıksız")}
                        >
                          <IconAlertTriangle size={16} />
                        </ActionIcon>
                        {it.status === "PORTFOLIO" && (
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            title="İptal"
                            onClick={() => handleStatusChange(it.id, "CANCELLED", "İptal")}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        )}
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <InstrumentCreateModal
        opened={opened}
        onClose={close}
        supplierId={supplierId}
      />
    </Stack>
  );
}

function InstrumentCreateModal({
  opened, onClose, supplierId,
}: { opened: boolean; onClose: () => void; supplierId: string }) {
  const today = new Date();
  const [form, setForm] = useState({
    instrument_type: "CHECK" as "CHECK" | "PROMISSORY_NOTE",
    serial_no: "",
    bank_name: "",
    bank_branch: "",
    drawer_name: "",
    issue_place: "",
    amount: 0 as number | string,
    issue_date: today as Date | null,
    due_date: new Date(today.getTime() + 30 * 24 * 3600 * 1000) as Date | null,
    notes: "",
  });
  // İki foto: çekin kendisi + "aslını elden teslim aldım" tutanağı.
  const [chequePhoto, setChequePhoto] = useState<File | null>(null);
  const [handoverPhoto, setHandoverPhoto] = useState<File | null>(null);
  const mutate = useCreateInstrument();
  const uploadImage = useUploadInstrumentImage();

  function patch(p: Partial<typeof form>) { setForm((f) => ({ ...f, ...p })); }

  async function submit() {
    if (!form.serial_no.trim()) {
      notifications.show({ message: "Seri no zorunlu", color: "red" });
      return;
    }
    if (!form.issue_date || !form.due_date) {
      notifications.show({ message: "Tarihler zorunlu", color: "red" });
      return;
    }
    const minor = Math.round(Number(form.amount) * 100);
    if (minor <= 0) {
      notifications.show({ message: "Tutar 0'dan büyük olmalı", color: "red" });
      return;
    }
    try {
      const created = await mutate.mutateAsync({
        instrument_type: form.instrument_type,
        supplier: supplierId,
        serial_no: form.serial_no.trim(),
        bank_name: form.bank_name,
        bank_branch: form.bank_branch,
        drawer_name: form.drawer_name,
        issue_place: form.issue_place,
        amount_minor: minor,
        issue_date: form.issue_date.toISOString().slice(0, 10),
        due_date: form.due_date.toISOString().slice(0, 10),
        notes: form.notes,
      });
      // Çek/senet oluştu → seçili fotoları yükle (id artık var).
      if (created?.id && chequePhoto) {
        await uploadImage.mutateAsync({ id: created.id, file: chequePhoto, kind: "CHEQUE_PHOTO" });
      }
      if (created?.id && handoverPhoto) {
        await uploadImage.mutateAsync({ id: created.id, file: handoverPhoto, kind: "HANDOVER_PHOTO" });
      }
      notifications.show({ message: "Çek/Senet kaydedildi", color: "green" });
      setChequePhoto(null);
      setHandoverPhoto(null);
      onClose();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  const isCheck = form.instrument_type === "CHECK";

  return (
    <Modal opened={opened} onClose={onClose} title="Yeni Çek / Senet" size="md">
      <Stack>
        <Select
          label="Tip"
          value={form.instrument_type}
          onChange={(v) => patch({ instrument_type: (v as any) || "CHECK" })}
          data={[
            { value: "CHECK", label: "Çek" },
            { value: "PROMISSORY_NOTE", label: "Senet" },
          ]}
        />
        <TextInput
          label={isCheck ? "Çek No" : "Senet No"}
          value={form.serial_no}
          onChange={(e) => patch({ serial_no: e.currentTarget.value })}
          required
        />
        {isCheck && (
          <>
            <TextInput
              label="Banka"
              value={form.bank_name}
              onChange={(e) => patch({ bank_name: e.currentTarget.value })}
            />
            <TextInput
              label="Şube"
              value={form.bank_branch}
              onChange={(e) => patch({ bank_branch: e.currentTarget.value })}
            />
          </>
        )}
        <TextInput
          label="Keşideci"
          placeholder="Genelde işletme adı"
          value={form.drawer_name}
          onChange={(e) => patch({ drawer_name: e.currentTarget.value })}
        />
        {isCheck && (
          <TextInput
            label="Keşide Yeri"
            placeholder="Çekin düzenlendiği yer (şehir/şube)"
            value={form.issue_place}
            onChange={(e) => patch({ issue_place: e.currentTarget.value })}
          />
        )}
        <NumberInput
          label="Tutar (TL)"
          value={form.amount}
          onChange={(v) => patch({ amount: v })}
          min={0}
          decimalScale={2}
          required
        />
        <Group grow>
          <DateInput
            label="Düzenleme Tarihi"
            value={form.issue_date}
            onChange={(v) => patch({ issue_date: v as any })}
            required
          />
          <DateInput
            label="Vade Tarihi"
            value={form.due_date}
            onChange={(v) => patch({ due_date: v as any })}
            required
          />
        </Group>
        <Textarea
          label="Notlar"
          value={form.notes}
          onChange={(e) => patch({ notes: e.currentTarget.value })}
          minRows={2}
        />
        {isCheck && (
          <Stack gap="xs">
            <Text size="sm" fw={500}>Fotoğraflar</Text>
            <Group>
              <FileButton onChange={setChequePhoto} accept="image/png,image/jpeg,image/webp,application/pdf">
                {(props) => (
                  <Button variant="light" {...props}>
                    {chequePhoto ? `Çek: ${chequePhoto.name}` : "Çek Fotoğrafı Seç"}
                  </Button>
                )}
              </FileButton>
              <FileButton onChange={setHandoverPhoto} accept="image/png,image/jpeg,image/webp,application/pdf">
                {(props) => (
                  <Button variant="light" {...props}>
                    {handoverPhoto ? `Teslim: ${handoverPhoto.name}` : "Aslını Elden Teslim Aldım Fotoğrafı"}
                  </Button>
                )}
              </FileButton>
            </Group>
          </Stack>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>İptal</Button>
          <Button onClick={submit} loading={mutate.isPending || uploadImage.isPending}>Kaydet</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Genel sekmesi (form)
// ---------------------------------------------------------------------------
function GeneralTab({ supplier }: { supplier: any }) {
  const update = useUpdate("suppliers");
  const [form, setForm] = useState({
    name: supplier.name || "",
    tax_id: supplier.tax_id || "",
    contact_name: supplier.contact_name || "",
    phone: supplier.phone || "",
    email: supplier.email || "",
    address: supplier.address || "",
    payment_terms_days: supplier.payment_terms_days || 0,
    credit_limit_minor: (supplier.credit_limit_minor || 0) / 100,
    notes: supplier.notes || "",
  });

  function patch(p: Partial<typeof form>) { setForm((f) => ({ ...f, ...p })); }

  const phoneError = validatePhoneTR(form.phone);
  const emailError = validateEmail(form.email);

  async function save() {
    if (phoneError || emailError) {
      notifications.show({ message: phoneError || emailError || "", color: "red" });
      return;
    }
    try {
      await update.mutateAsync({
        id: supplier.id,
        ...form,
        credit_limit_minor: Math.round(Number(form.credit_limit_minor || 0) * 100),
      });
      notifications.show({ message: "Kaydedildi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Card withBorder padding="md">
      <Stack>
        <TextInput
          label="Ad"
          value={form.name}
          onChange={(e) => patch({ name: e.currentTarget.value })}
          required
        />
        <Group grow>
          <TextInput
            label="VKN / TCKN"
            value={form.tax_id}
            onChange={(e) => patch({ tax_id: e.currentTarget.value })}
          />
          <TextInput
            label="İletişim Kişisi"
            value={form.contact_name}
            onChange={(e) => patch({ contact_name: e.currentTarget.value })}
          />
        </Group>
        <Group grow>
          <TextInput
            label="Telefon"
            type="tel"
            inputMode="tel"
            placeholder="+90 555 123 45 67"
            value={form.phone}
            onChange={(e) => patch({ phone: e.currentTarget.value })}
            error={form.phone ? phoneError : null}
          />
          <TextInput
            label="E-posta"
            type="email"
            inputMode="email"
            value={form.email}
            onChange={(e) => patch({ email: e.currentTarget.value })}
            error={form.email ? emailError : null}
          />
        </Group>
        <Textarea
          label="Adres"
          value={form.address}
          onChange={(e) => patch({ address: e.currentTarget.value })}
          minRows={2}
        />
        <Group grow>
          <NumberInput
            label="Vade (gün)"
            value={form.payment_terms_days}
            onChange={(v) => patch({ payment_terms_days: Number(v || 0) })}
            min={0}
          />
          <NumberInput
            label="Kredi Limiti (TL) — 0 = limitsiz"
            value={form.credit_limit_minor}
            onChange={(v) => patch({ credit_limit_minor: Number(v || 0) })}
            min={0}
            decimalScale={2}
          />
        </Group>
        <Textarea
          label="Notlar"
          value={form.notes}
          onChange={(e) => patch({ notes: e.currentTarget.value })}
          minRows={2}
        />
        <Group justify="flex-end">
          <Button onClick={save} loading={update.isPending}>Kaydet</Button>
        </Group>
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Satın Alma Geçmişi sekmesi
// ---------------------------------------------------------------------------
// Statü Türkçe etiket + renk (kural: SON adım = yeşil, bir önceki = mavi).
const PO_ST: Record<string, { label: string; color: string }> = {
  DRAFT: { label: "Taslak", color: "gray" },
  SENT: { label: "Gönderildi", color: "grape" },
  PARTIAL: { label: "Kısmi Teslim", color: "orange" },
  RECEIVED: { label: "Teslim Alındı", color: "blue" },
  COMPLETED: { label: "Tamamlandı", color: "green" },
  CLOSED: { label: "Kapatıldı", color: "gray" },
  CANCELLED: { label: "İptal", color: "red" },
};
const GR_ST: Record<string, { label: string; color: string }> = {
  DRAFT: { label: "Taslak", color: "gray" },
  CONFIRMED: { label: "Onaylandı", color: "blue" },
  CANCELLED: { label: "İptal", color: "red" },
};
const INV_ST: Record<string, { label: string; color: string }> = {
  DRAFT: { label: "Taslak", color: "gray" },
  POSTED: { label: "İşlendi", color: "blue" },
  PARTIALLY_PAID: { label: "Kısmen Ödendi", color: "yellow" },
  PAID: { label: "Ödendi", color: "green" },
  CANCELLED: { label: "İptal", color: "red" },
};
function StBadge({ map, status }: { map: Record<string, { label: string; color: string }>; status: string }) {
  const s = map[status] || { label: status, color: "gray" };
  return <Badge color={s.color} variant="light">{s.label}</Badge>;
}

const HISTORY_PAGE = 5;  // başlangıçta gösterilen kayıt; "Daha Fazla" ile +5

function HistoryTab({ supplierId }: { supplierId: string }) {
  const { data: pos } = useResource<any>("purchase-orders", { filters: { supplier: supplierId } });
  const { data: invs } = useResource<any>("supplier-invoices", { filters: { supplier: supplierId } });
  const { data: grs } = useResource<any>("goods-receipts", { filters: { supplier: supplierId } });

  // Backend zaten -created_at sıralı döndürür (en yeni üstte).
  const poList: any[] = pos?.results || pos || [];
  const invList: any[] = invs?.results || invs || [];
  const grList: any[] = grs?.results || grs || [];

  // Her bölüm için görünen kayıt sayısı — "Daha Fazla Göster" ile +5 (ekran kasılmasın).
  const [poN, setPoN] = useState(HISTORY_PAGE);
  const [grN, setGrN] = useState(HISTORY_PAGE);
  const [invN, setInvN] = useState(HISTORY_PAGE);

  // Tıklanan kaydın detayını mevcut drawer/modal bileşenleriyle aç.
  const [detailPo, setDetailPo] = useState<any | null>(null);
  const [detailGr, setDetailGr] = useState<any | null>(null);
  const [detailInv, setDetailInv] = useState<any | null>(null);
  const rowStyle = { cursor: "pointer" };

  return (
    <Stack gap="md">
      <HistoryBlock
        title="Satın Alma Siparişleri" icon={<IconReceipt2 size={18} />}
        total={poList.length} shown={poN} onMore={() => setPoN((n) => n + HISTORY_PAGE)}
      >
        {poList.length === 0 ? <EmptyHistoryRow text="PO yok" /> : poList.slice(0, poN).map((po) => (
          <Table.Tr key={po.id} style={rowStyle} onClick={() => setDetailPo(po)}>
            <Table.Td>{formatDate(po.created_at)}</Table.Td>
            <Table.Td fw={500}>{po.po_number}</Table.Td>
            <Table.Td><StBadge map={PO_ST} status={po.status} /></Table.Td>
            <Table.Td ta="right"><MoneyDisplay minor={po.total_minor} /></Table.Td>
          </Table.Tr>
        ))}
      </HistoryBlock>
      <HistoryBlock
        title="Mal Girişleri" icon={<IconCash size={18} />}
        total={grList.length} shown={grN} onMore={() => setGrN((n) => n + HISTORY_PAGE)}
      >
        {grList.length === 0 ? <EmptyHistoryRow text="GR yok" /> : grList.slice(0, grN).map((gr) => (
          <Table.Tr key={gr.id} style={rowStyle} onClick={() => setDetailGr(gr)}>
            <Table.Td>{formatDate(gr.received_at || gr.created_at)}</Table.Td>
            <Table.Td fw={500}>{gr.gr_number}</Table.Td>
            <Table.Td>
              {/* Onaylı + faturalanmış → "Faturalandı" (yeşil, liste deseniyle aynı). */}
              {gr.status === "CONFIRMED" && gr.is_invoiced
                ? <Badge color="green" variant="light">Faturalandı</Badge>
                : <StBadge map={GR_ST} status={gr.status} />}
            </Table.Td>
            <Table.Td ta="right">{gr.delivery_note_number || "—"}</Table.Td>
          </Table.Tr>
        ))}
      </HistoryBlock>
      <HistoryBlock
        title="Faturalar" icon={<IconFileInvoice size={18} />}
        total={invList.length} shown={invN} onMore={() => setInvN((n) => n + HISTORY_PAGE)}
      >
        {invList.length === 0 ? <EmptyHistoryRow text="Fatura yok" /> : invList.slice(0, invN).map((inv) => (
          <Table.Tr key={inv.id} style={rowStyle} onClick={() => setDetailInv(inv)}>
            <Table.Td>{formatDate(inv.invoice_date)}</Table.Td>
            <Table.Td fw={500}>{inv.invoice_number}</Table.Td>
            <Table.Td><StBadge map={INV_ST} status={inv.status} /></Table.Td>
            <Table.Td ta="right"><MoneyDisplay minor={inv.total_minor} /></Table.Td>
          </Table.Tr>
        ))}
      </HistoryBlock>

      <PurchaseOrderDetailDrawer po={detailPo} onClose={() => setDetailPo(null)} />
      <GoodsReceiptDetailDrawer
        gr={detailGr}
        onClose={() => setDetailGr(null)}
        onUpdated={(fresh) => setDetailGr(fresh)}
      />
      <SupplierInvoiceDetailModal invoice={detailInv} onClose={() => setDetailInv(null)} />
    </Stack>
  );
}

function HistoryBlock({
  title, icon, children, total = 0, shown = 0, onMore,
}: {
  title: string; icon: React.ReactNode; children: React.ReactNode;
  total?: number; shown?: number; onMore?: () => void;
}) {
  const hasMore = total > shown;
  return (
    <Card padding="sm" withBorder>
      <Group gap="xs" mb="xs">
        {icon}
        <Text fw={600}>{title}</Text>
        {total > 0 && <Text size="xs" c="dimmed">({Math.min(shown, total)}/{total})</Text>}
      </Group>
      <Table>
        <Table.Tbody>{children}</Table.Tbody>
      </Table>
      {hasMore && onMore && (
        <Group justify="center" mt="xs">
          <Button variant="subtle" size="xs" onClick={onMore}>
            Daha Fazla Göster ({total - shown})
          </Button>
        </Group>
      )}
    </Card>
  );
}

function EmptyHistoryRow({ text }: { text: string }) {
  return (
    <Table.Tr>
      <Table.Td colSpan={4} ta="center"><Text c="dimmed" size="sm">{text}</Text></Table.Td>
    </Table.Tr>
  );
}
